import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";

import { map, pairwise, startWith } from "rxjs/operators";

import { DffFormControl, DffFormControlOptions, DffFormControlType, DffOprindeligAktuelVaerdiFormGroup } from "@dffedb/forms";
import { DffOprindeligAktuelVaerdi } from "@dffedb/util";
import { NoteBruger, NoteFormBuilder, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { MaalerAflaesningFormBuilder } from "../maaler-aflaesning/maaler-aflaesning-form-builder";
import { MaalerAflaesningMedNoter } from "../model/maaler-aflaesning-med-noter.model";
import { MaalerAflaesning } from "../model/maaler-aflaesning.model";
import { MaalerType } from "../model/maaler-type.model";
import { Maaler } from "../model/maaler.model";
import { TaellevaerkAflaesning } from "../model/taellevaerk-aflaesning.model";
import { TaellevaerkType } from "../model/taellevaerk-type.model";

export class MaalerOprettelseFormBuilder {
    public static maalerAflaesningMedNoterToFormGroup(
        maalerAflaesning: MaalerAflaesningMedNoter,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const result = this.maalerAflaesningToFormGroup(maalerAflaesning, maalerePaaLager, maalerTyper);
        const noter = NoteFormBuilder.noterToFormArray(
            maalerAflaesning.noter,
            noteOmraader,
            noteHenvendelsestyper,
            noteOpgavestyringStatusser,
            noteBrugere
        );
        result.setControl("noter", noter);
        return result;
    }

    public static maalerAflaesningToFormGroup(
        maalerAflaesning: MaalerAflaesning,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[]
    ): UntypedFormGroup {
        const maalerType = maalerTyper.find((t) => t.id === maalerAflaesning.maalerTypeId.aktuel) || ({ taellevaerker: [] } as MaalerType);
        const result = MaalerAflaesningFormBuilder.maalerAflaesningToFormGroup(maalerAflaesning, maalerType, false);
        const maalerNrFormGroup = this.opretMaalerNr(maalerAflaesning.maalerNr, maalerePaaLager, false);
        result.setControl("maalerNr", maalerNrFormGroup);
        result.setControl("maalerNrAlphaNumeric", new UntypedFormControl(false));
        result.setControl("maalerTypeId", this.opretMaalerTypeId(maalerAflaesning.maalerTypeId, maalerTyper));

        this.maalerNrChanged(result, maalerePaaLager, maalerTyper, "", maalerAflaesning.maalerNr.aktuel);
        result
            .get("maalerNr.aktuel")
            .valueChanges.pipe(
                startWith(maalerAflaesning.maalerNr.aktuel),
                map((value) => value + ""), // Kan være number eller string afhængig af 'maalerNrAlphaNumeric'
                pairwise()
            )
            .subscribe(([glMaalerNr, nyMaalerNr]) => {
                this.maalerNrChanged(result, maalerePaaLager, maalerTyper, glMaalerNr, nyMaalerNr);
            });

        this.maalerTypeIdChanged(result, maalerTyper, maalerAflaesning.maalerTypeId.aktuel);
        result.get("maalerTypeId.aktuel").valueChanges.subscribe((nyMaalerTypeId: number) => {
            this.maalerTypeIdChanged(result, maalerTyper, nyMaalerTypeId);
        });

        result.get("maalerNrAlphaNumeric").valueChanges.subscribe((value: boolean) => {
            (result.get("maalerNr.aktuel") as DffFormControl).setOptions(
                this.opretMaalerNrOptions(maalerNrFormGroup, maalerePaaLager, value)
            );
        });
        return result;
    }

    public static formGroupToMaalerAflaesningMedNoter(formGroup: UntypedFormGroup): MaalerAflaesningMedNoter {
        return MaalerAflaesningFormBuilder.formGroupToMaalerAflaesningMedNoter(formGroup);
    }

    private static opretMaalerNr(
        value: DffOprindeligAktuelVaerdi<string>,
        maalerePaaLager: Maaler[],
        isAlphaNumeric: boolean
    ): DffOprindeligAktuelVaerdiFormGroup {
        const result = new DffOprindeligAktuelVaerdiFormGroup(value, {}, [Validators.required, Validators.maxLength(12)]);
        const options = this.opretMaalerNrOptions(result, maalerePaaLager, isAlphaNumeric);
        result.setOptions(options);
        return result;
    }

    private static opretMaalerTypeId(
        value: DffOprindeligAktuelVaerdi<number>,
        maalerTyper: MaalerType[]
    ): DffOprindeligAktuelVaerdiFormGroup {
        // En ny måler, som endnu ikke har en målertype, modtager vi værdien 0 fra FOF.
        // Denne ønsker vi at opfatte som 'null', da vi så kan benytte Required-validator
        // ...men kun hvis der ikke findes en målertype med id=0
        const valueFixed: DffOprindeligAktuelVaerdi<number> = {
            oprindelig: value.oprindelig === 0 && !maalerTyper?.some((mt) => mt.id === 0) ? null : value.oprindelig,
            aktuel: value.aktuel === 0 && !maalerTyper?.some((mt) => mt.id === 0) ? null : value.aktuel
        };
        const options = this.opretMaalerTypeIdOptions(maalerTyper, false);
        const result = new DffOprindeligAktuelVaerdiFormGroup(valueFixed, options, [Validators.required]);
        return result;
    }

    private static maalerNrChanged(
        maalerAflaesning: UntypedFormGroup,
        maalerePaaLager: Maaler[],
        maalerTyper: MaalerType[],
        glMaalerNr: string,
        nyMaalerNr: string
    ): void {
        const maalerTypeId = maalerAflaesning.get("maalerTypeId.aktuel") as DffFormControl;
        const glMaaler = maalerePaaLager.find((m) => m.maalerNr === glMaalerNr);
        const nyMaaler = maalerePaaLager.find((m) => m.maalerNr === nyMaalerNr);
        if (nyMaaler) {
            maalerTypeId.setValue(nyMaaler.maalerTypeNr);
        } else if (glMaaler) {
            // Gammelt målernr var kendt og havde derfor automatisk udfyldt målertype.
            // Det nye målernr er imidlertid ikke kendt, så vi blanker målertype.
            maalerTypeId.setValue(null);
        }
        maalerTypeId.setOptions(this.opretMaalerTypeIdOptions(maalerTyper, !!nyMaaler));
    }

    private static maalerTypeIdChanged(maalerAflaesning: UntypedFormGroup, maalerTyper: MaalerType[], maalerTypeId: number): void {
        const nuvaerendeMaalerAflaesning = MaalerAflaesningFormBuilder.formGroupToMaalerAflaesning(maalerAflaesning);
        const maalerType = maalerTyper.find((t) => t.id === maalerTypeId);
        const taellevaerkTyper = (maalerType && maalerType.taellevaerker) || [];
        const taellevaerkAflaesninger = taellevaerkTyper.map((t) => this.hentTaellevaerkVaerdi(nuvaerendeMaalerAflaesning, t));
        const taellevaerkFormArray = MaalerAflaesningFormBuilder.taellevaerkAflaesningerToFormArray(
            taellevaerkAflaesninger,
            taellevaerkTyper,
            false
        );
        maalerAflaesning.setControl("taellevaerker", taellevaerkFormArray);
    }

    private static opretMaalerNrOptions(
        maalerNrFormGroup: DffOprindeligAktuelVaerdiFormGroup,
        maalerePaaLager: Maaler[],
        isAlphaNumeric: boolean
    ): DffFormControlOptions {
        const dropdownListOptions = maalerePaaLager.map((m) => ({ key: m.maalerNr, value: m.maalerNr }));
        const dropdownListOptions$ = maalerNrFormGroup
            .get("aktuel")
            .valueChanges.pipe(map((v) => (!v ? [] : dropdownListOptions.filter((o) => o.value.startsWith(v) && o.value !== v))));
        return {
            placeholder: "Målernummer",
            controlType: isAlphaNumeric ? DffFormControlType.Autocomplete : DffFormControlType.AutocompleteNumeric,
            dropdownListOptions: dropdownListOptions$
        };
    }

    private static opretMaalerTypeIdOptions(maalerTyper: MaalerType[], disabled: boolean): DffFormControlOptions {
        const dropdownListOptions = maalerTyper
            .filter((t) => !t.passiv)
            .sort((t1, t2) => t1.id - t2.id)
            .map((t) => ({ key: t.id, value: t.id + " - " + t.tekst }));
        return {
            placeholder: "Målertype",
            controlType: DffFormControlType.DropdownList,
            dropdownListOptions,
            disabled
        };
    }

    private static hentTaellevaerkVaerdi(maalerAflaesning: MaalerAflaesning, taellevaerkType: TaellevaerkType): TaellevaerkAflaesning {
        const taellevaerkAflaesning = maalerAflaesning.taellevaerker.find((t) => t.enhedType === taellevaerkType.enhedType);
        const aflaesningAktuel = (taellevaerkAflaesning && taellevaerkAflaesning.aflaesning.aktuel) || 0;
        const aflaesningOprindelig = (taellevaerkAflaesning && taellevaerkAflaesning.aflaesning.oprindelig) || 0;
        const historiskAflaesning = (taellevaerkAflaesning && taellevaerkAflaesning.historiskAflaesning) || 0;
        const enhedType = taellevaerkType.enhedType;
        return {
            aflaesning: {
                oprindelig: aflaesningOprindelig,
                aktuel: aflaesningAktuel
            },
            historiskAflaesning,
            enhedType
        };
    }
}
